import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
/* eslint-disable max-len */
import { VehicleIAMPartCategoryTreeItem } from '@1po/1po-bff-fe-spec/generated/catalog/category/model/VehicleIAMPartCategoryTreeItem';
import styled from 'styled-components';
import { ROUTER_CATALOG_SEARCH, ROUTER_CATALOG_TIRES, ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import Loader from 'components/Loader';
import {
  useFetchIAMTechnicalDataTree,
  useFetchVehicle,
  useUpdateVehicleSearchHistory,
} from 'domains/catalog/Catalog.requests';
import {
  getExplodedIAMTree,
  getLastSearchedVehicleKey,
  getSearchVehicleResult,
  resetPreviousTechnicalCriteria,
  setLastSearchedVehicleKey,
} from 'domains/catalog/Catalog.store';
import {
  ActiveTabParam,
  CAR_PARTS_TAB,
  IamCatalogTabsType,
  PARAM_LABOR_TIME,
  PARAM_SPARE_PARTS,
  TECHNICAL_DATA_TAB,
  TechnicalDataIdParam,
  VehicleCountry,
  VehicleIdentifiedBy,
} from 'domains/catalog/Catalog.types';
import { getIAMCatalogSourcePaths } from 'domains/catalog/Catalog.utils';
import { useFetchIAMAvailableModules } from 'domains/catalog/CatalogIam.requests';
import { useFetchEstimateSettingsSubscription } from 'domains/estimate/Estimate.requests';
import { useFetchIAMMaintenancePlan } from 'domains/maintenancePlan/MaintenancePlan.requests';
import { useFetchIAMTires } from 'domains/tires/Tire.requests';
import { getIamCatalogTabsView, setIamCatalogTabsView } from 'domains/user';
import { CatalogTabs } from 'pages/CatalogPage/common';
import { BrandsSwitch } from 'pages/CatalogPage/IAM/BrandsSwitch/BrandsSwitch';
import CarPartGroupsSection from 'pages/CatalogPage/IAM/CarPartGroupsSection';
import FullTextSearchResult from 'pages/CatalogPage/IAM/FullTextSearchResult';
import SubcategorySection from 'pages/CatalogPage/IAM/SubcategorySection';
import { Container, Empty, Flex, indentPadding, MarginBox } from 'UI';
import { ERROR, getData, normalizeVIN, NOT_FOUND, SearchData } from 'utils';
import { useWindowSize } from 'utils/hooks/useWindowSize';
import { CarCategorySection } from './CarCategorySection/CarCategorySection';
import IAMTires from './Tires';
import { getCatalogContainerSize } from '../common/catalogContainerSize';
import QuickAccess from '../common/QuickAccess';
import { useResetQuickAccessCategories } from '../common/QuickAccess/QuickAccess';

const IAMCatalogColumn = styled.div`
  width: 100%;
`;

const IAMCatalogScrollableContent = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

export const CatalogTabsIAM = ({
  tree,
  tabKey,
  setTabKey,
}: {
  tree: SearchData<VehicleIAMPartCategoryTreeItem[]> | undefined;
  tabKey: IamCatalogTabsType;
  setTabKey: (t: IamCatalogTabsType) => void;
}) => {
  const { t } = useTranslation();
  //eslint-disable-next-line
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activeTab = params.get(ActiveTabParam);

  useEffect(() => {
    if (tabKey === TECHNICAL_DATA_TAB) {
      return;
    }
    if (activeTab === PARAM_SPARE_PARTS || activeTab === PARAM_LABOR_TIME) {
      setTabKey(CAR_PARTS_TAB);
    }
  }, [activeTab, setTabKey, tabKey]);

  return (
    <DataContainer
      data={tree?.searchStatus}
      ErrorState={() => (
        <ErrorWithLabel
          label={t('catalog.parts.backend_error', 'Car parts are temporarily unavailable, please try again later.')}
        />
      )}
    >
      <Switch>
        <Route path={getIAMCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}`)} exact>
          <IAMCatalogScrollableContent>
            <BrandsSwitch />
            <CarPartGroupsSection />
          </IAMCatalogScrollableContent>
        </Route>
        <Route
          path={getIAMCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId/:subcategoryId?`)}
          exact
        >
          <IAMCatalogScrollableContent>
            <BrandsSwitch />
            <CarCategorySection />
          </IAMCatalogScrollableContent>
        </Route>
        <Route
          path={getIAMCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId/:categoryId/:subcategoryId`)}
          exact
        >
          <IAMCatalogScrollableContent>
            <SubcategorySection />
          </IAMCatalogScrollableContent>
        </Route>
        <Route path={'*'}>
          <Redirect to={'/'} />
        </Route>
      </Switch>
    </DataContainer>
  );
};
const IAMCatalogPage = ({ setCatalogShowMenu }: { setCatalogShowMenu: (v: boolean) => void }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { query } = useParams<{
    query: string;
  }>();
  const tabKey = useSelector(getIamCatalogTabsView);
  const result = useSelector(
    (state: RootState) =>
      getSearchVehicleResult(state, query) ??
      getSearchVehicleResult(state, query && normalizeVIN(query).substring(0, 17)),
  );
  const searchStatus = result?.searchVehicleStatus;
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const params = new URLSearchParams(location.search);
  const tree = getData(useSelector((state: RootState) => getExplodedIAMTree(state, lastSearchedVehicleKey)));
  const { width: windowWidth } = useWindowSize();
  const catalogContainerSize = getCatalogContainerSize(windowWidth);

  const { t } = useTranslation();

  const setTabKey = (tab: IamCatalogTabsType) => {
    dispatch(setIamCatalogTabsView(tab));
  };

  useEffect(() => {
    if (
      !lastSearchedVehicleKey ||
      (lastSearchedVehicleKey !== query && lastSearchedVehicleKey && !lastSearchedVehicleKey.includes(query))
    ) {
      dispatch(resetPreviousTechnicalCriteria());
      dispatch(setLastSearchedVehicleKey(query));
    }
  }, [dispatch, query, lastSearchedVehicleKey]);

  useEffect(() => {
    if (lastSearchedVehicleKey !== query && lastSearchedVehicleKey && lastSearchedVehicleKey.includes(query)) {
      const replacedUrl = history.location.pathname.replace(query, lastSearchedVehicleKey);
      history.replace(replacedUrl);
    }
  }, [history, query, lastSearchedVehicleKey]);

  useEffect(() => {
    if (tabKey !== TECHNICAL_DATA_TAB) {
      params.delete(TechnicalDataIdParam);
      history.replace(`${location.pathname}?${params}`);
    }
    // eslint-disable-next-line
  }, [tabKey]);

  useEffect(() => {
    const country = result?.vehicleDetail?.country;
    const vehicleIdentifiedBy = result?.vehicleDetail?.vehicleIdentifiedBy;
    const shouldUpdateCountry = params.get(VehicleCountry) !== country && country;
    const shouldUpdateIdentification = params.get(VehicleIdentifiedBy) !== vehicleIdentifiedBy && vehicleIdentifiedBy;

    if (shouldUpdateCountry || shouldUpdateIdentification) {
      if (shouldUpdateCountry) {
        params.set(VehicleCountry, country);
      }

      if (shouldUpdateIdentification) {
        params.set(VehicleIdentifiedBy, vehicleIdentifiedBy);
      }
      history.replace(`${location.pathname}?${params}`);
    }
  });

  useFetchEstimateSettingsSubscription();
  useFetchVehicle(query, searchStatus);
  useUpdateVehicleSearchHistory(result?.vehicleDetail);
  useFetchIAMAvailableModules(result?.vehicleDetail);
  useFetchIAMTechnicalDataTree(result?.vehicleDetail);
  useFetchIAMMaintenancePlan(result?.vehicleDetail);
  useFetchIAMTires(result?.vehicleDetail?.iamVehicle?.versionCode);
  useResetQuickAccessCategories();

  if (!result || !result.vehicleDetail) {
    switch (searchStatus) {
      case NOT_FOUND:
        return (
          <MarginBox my={220}>
            <Empty />
          </MarginBox>
        );
      case ERROR:
        return (
          <MarginBox my={220}>
            <ErrorWithLabel
              label={t(
                'catalog.parts.search.by_vin_or_vrn.error',
                'Sorry, we could not find any vehicle associated, please try again later.',
              )}
            />
          </MarginBox>
        );
      default:
        return <Loader />;
    }
  }

  const catalogTabsProps = {
    result: result.vehicleDetail,
    vehicleImage: result?.vehicleDetail?.imageUrl,
    setCatalogShowMenu,
    catalogPage: 'OTHER_BRANDS_CATALOG' as const,
    query,
    vehicleRouteChildren: (
      <>
        <QuickAccess />
        <CatalogTabsIAM tree={tree} tabKey={tabKey} setTabKey={setTabKey} />
      </>
    ),
    tiresRouteChildren: (
      <>
        <QuickAccess />
        <IAMTires />
      </>
    ),
  };

  const renderCatalogTabs = () => <CatalogTabs {...catalogTabsProps} />;

  return (
    <Switch>
      <Route path={getIAMCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}${ROUTER_CATALOG_SEARCH}/:label`)}>
        <Container alignText={'left'} size={catalogContainerSize}>
          <IAMCatalogScrollableContent>
            <FullTextSearchResult />
          </IAMCatalogScrollableContent>
        </Container>
      </Route>
      <Route path={getIAMCatalogSourcePaths(`/:query${ROUTER_CATALOG_TIRES}`)}>
        <Container alignText={'left'} padding={indentPadding} size={catalogContainerSize}>
          <IAMCatalogColumn>{renderCatalogTabs()}</IAMCatalogColumn>
        </Container>
      </Route>
      <Route
        path={getIAMCatalogSourcePaths(
          `/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId?/:categoryId?/:subcategoryId?/:plateId?`,
        )}
      >
        <Container alignText={'left'} padding={indentPadding} size={catalogContainerSize} data-cy={'iam-catalog-page'}>
          <IAMCatalogColumn>{renderCatalogTabs()}</IAMCatalogColumn>
        </Container>
      </Route>
      <Route path={getIAMCatalogSourcePaths(`/:query`)}>
        <Container alignText={'left'} padding={indentPadding} size={catalogContainerSize}>
          <IAMCatalogColumn>
            <Flex>{renderCatalogTabs()}</Flex>
          </IAMCatalogColumn>
        </Container>
      </Route>
      <Route path={'*'}>
        <Redirect to={'/'} />
      </Route>
    </Switch>
  );
};

export default IAMCatalogPage;
